// The form that records one observed competitor campaign. The server is the
// validator: it answers 400 with { error, details: [{ path, message }] } and
// each message is shown under its own field. The form only catches empty
// required fields and a non-numeric engagement before sending. Engagement
// left empty is sent as null, which the API keeps as "not recorded".
import { useEffect, useId, useRef, useState, type ChangeEvent, type FormEvent, type ReactNode } from "react";
import { Notice } from "@dashboard-intelligent/ui";
import type { Campaign, Competitor } from "@dashboard-intelligent/api/src/modules/benchmark/types";
import type { IndustrySlug } from "@dashboard-intelligent/api/src/modules/industry/types";
import { benchmarkApi, fieldIssues } from "./api";
import {
  EMPTY_DRAFT,
  STATUS_ORDER,
  STATUS_TEXT,
  fieldOf,
  issueText,
  toInput,
  type CampaignDraft,
  type DraftField,
  type FieldErrors,
} from "./model";

export interface CampaignFormProps {
  slug: IndustrySlug;
  competitors: Competitor[];
  channels: { channel: string; label: string }[];
  /** Called after a 201, so the page reloads the board. */
  onSaved: (campaign: Campaign) => void;
}

type Outcome = { kind: "idle" } | { kind: "saved"; title: string } | { kind: "failed"; message: string };

export function CampaignForm({ slug, competitors, channels, onSaved }: CampaignFormProps) {
  const id = useId();
  const [draft, setDraft] = useState<CampaignDraft>(EMPTY_DRAFT);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [saving, setSaving] = useState(false);
  const [outcome, setOutcome] = useState<Outcome>({ kind: "idle" });
  const formRef = useRef<HTMLFormElement>(null);
  // After a rejected send, move focus to the first field that needs fixing.
  const [focusNonce, setFocusNonce] = useState(0);
  useEffect(() => {
    if (focusNonce === 0) return;
    formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus();
  }, [focusNonce]);

  const set = (field: DraftField) => (event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const value = event.target.value;
    setDraft((d) => ({ ...d, [field]: value }));
    if (errors[field]) setErrors((e) => ({ ...e, [field]: undefined }));
  };

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (saving) return;
    const parsed = toInput(draft);
    if (!parsed.ok) {
      setErrors(parsed.errors);
      setOutcome({ kind: "idle" });
      setFocusNonce((n) => n + 1);
      return;
    }
    setSaving(true);
    setErrors({});
    setOutcome({ kind: "idle" });
    try {
      const campaign = await benchmarkApi.recordCampaign(slug, parsed.input);
      // Keep who recorded it, clear the observation itself.
      setDraft({ ...EMPTY_DRAFT, recordedBy: draft.recordedBy });
      setOutcome({ kind: "saved", title: campaign.title });
      onSaved(campaign);
    } catch (err) {
      const issues = fieldIssues(err);
      const next: FieldErrors = {};
      const loose: string[] = [];
      for (const issue of issues) {
        const field = fieldOf(issue.path);
        if (field && !next[field]) next[field] = issueText(issue.message);
        else if (!field) loose.push(issueText(issue.message));
      }
      setErrors(next);
      if (Object.keys(next).length > 0) setFocusNonce((n) => n + 1);
      const message = err instanceof Error ? err.message : "Kampanye tidak dapat disimpan.";
      setOutcome({
        kind: "failed",
        message:
          issues.length > 0
            ? `Periksa pesan pada kolom yang ditandai di atas.${loose.length > 0 ? ` ${loose.join(" ")}` : ""}`
            : message,
      });
    } finally {
      setSaving(false);
    }
  }

  const field = (name: DraftField, label: string, control: (props: ControlProps) => ReactNode, hint?: string) => (
    <Field id={`${id}${name}`} label={label} error={errors[name]} hint={hint} control={control} />
  );

  return (
    <form ref={formRef} className="viz-form" noValidate onSubmit={submit} aria-busy={saving || undefined}>
      <div className="field-row">
        {field("competitorId", "Pesaing", (p) => (
          <select {...p} value={draft.competitorId} onChange={set("competitorId")}>
            <option value="">Pilih pesaing</option>
            {competitors.map((c) => (
              <option key={c.id} value={c.id}>
                {c.isSelf ? `${c.name} (sendiri)` : c.name}
              </option>
            ))}
          </select>
        ))}
        {field("channel", "Kanal", (p) => (
          <select {...p} value={draft.channel} onChange={set("channel")}>
            <option value="">Pilih kanal</option>
            {channels.map((c) => (
              <option key={c.channel} value={c.channel}>
                {c.label}
              </option>
            ))}
          </select>
        ))}
      </div>
      <div className="field-row" data-cols="1">
        {field("title", "Judul kampanye", (p) => (
          <input {...p} type="text" maxLength={160} value={draft.title} onChange={set("title")} />
        ))}
      </div>
      <div className="field-row">
        {field("status", "Status", (p) => (
          <select {...p} value={draft.status} onChange={set("status")}>
            {STATUS_ORDER.map((s) => (
              <option key={s} value={s}>
                {STATUS_TEXT[s]}
              </option>
            ))}
          </select>
        ))}
        {field("recordedBy", "Dicatat oleh", (p) => (
          <input {...p} type="text" maxLength={120} autoComplete="name" value={draft.recordedBy} onChange={set("recordedBy")} />
        ))}
      </div>
      <div className="field-row">
        {field("startedOn", "Tanggal mulai", (p) => <input {...p} type="date" value={draft.startedOn} onChange={set("startedOn")} />)}
        {field(
          "endedOn",
          "Tanggal selesai (opsional)",
          (p) => <input {...p} type="date" value={draft.endedOn} onChange={set("endedOn")} />,
          "Kosongkan bila masih berjalan.",
        )}
      </div>
      <div className="field-row" data-cols="1">
        {field(
          "angle",
          "Pesan utama",
          (p) => <textarea {...p} maxLength={400} value={draft.angle} onChange={set("angle")} />,
          "Apa yang ditawarkan atau dikatakan kampanye ini, dalam satu kalimat.",
        )}
      </div>
      <div className="field-row">
        {field(
          "focus",
          "Fokus (opsional)",
          (p) => <input {...p} type="text" value={draft.focus} onChange={set("focus")} />,
          "Kategori atau program, pisahkan dengan koma.",
        )}
        {field(
          "engagement",
          "Engagement (opsional)",
          (p) => <input {...p} type="number" inputMode="numeric" min={0} step={1} value={draft.engagement} onChange={set("engagement")} />,
          "Kosongkan bila tidak dicatat. Kosong bukan nol.",
        )}
      </div>
      <div className="field-row" data-cols="1">
        {field(
          "evidence",
          "Bukti",
          (p) => <input {...p} type="text" maxLength={500} value={draft.evidence} onChange={set("evidence")} />,
          "Tautan unggahan atau halaman, atau catatan di mana Anda melihatnya.",
        )}
      </div>

      {outcome.kind === "failed" ? (
        <Notice tone="danger" title="Kampanye belum tersimpan">
          <p>{outcome.message}</p>
        </Notice>
      ) : outcome.kind === "saved" ? (
        <Notice tone="success" title="Kampanye tersimpan">
          <p>{outcome.title} sudah masuk ke papan.</p>
        </Notice>
      ) : null}

      <div className="viz-form-actions">
        <button type="submit" disabled={saving} aria-busy={saving || undefined} data-state={saving ? "loading" : undefined}>
          {saving ? "Menyimpan" : "Simpan kampanye"}
        </button>
      </div>
    </form>
  );
}

interface ControlProps {
  id: string;
  "aria-invalid": true | undefined;
  "aria-describedby": string;
}

function Field({
  id,
  label,
  error,
  hint,
  control,
}: {
  id: string;
  label: string;
  error?: string;
  hint?: string;
  control: (props: ControlProps) => ReactNode;
}) {
  const hintId = `${id}hint`;
  return (
    <div className="field">
      <label className="field-label" htmlFor={id}>
        {label}
      </label>
      {control({ id, "aria-invalid": error ? true : undefined, "aria-describedby": hintId })}
      <p className="field-hint" id={hintId} data-tone={error ? "danger" : undefined}>
        {error ?? hint ?? ""}
      </p>
    </div>
  );
}
